import React from "react";
import PropTypes from "prop-types";

// Tone per confidence level, using the same Tailwind palette as the verdict band.
const TONE = {
  high: "border-success bg-success-soft text-success",
  medium: "border-warning bg-warning-soft text-warning",
  low: "border-danger bg-danger-soft text-danger",
};
const LABEL = { high: "High", medium: "Medium", low: "Low" };

// Which quantity the ±% rates. "projection" = how far past month-end projections landed from the
// realized month total; "horizon_mape" = day-to-day model error at the remaining-days horizon.
// Naming it stops the ± reading as a generic accuracy number.
const BASIS_TEXT = {
  projection: "month-end",
  horizon_mape: "day-to-day",
};

// ConfidenceBadge is a small muted pill shown next to the verdict, but ONLY once
// the forecast confidence is actually rated. It stays hidden while "not_yet_rated".
//
// It makes exactly ONE claim: how well the forecast has been scoring, and against which
// quantity. The projection range's provenance (range_label / leave-one-out coverage /
// model-band coverage) deliberately does NOT live here — it describes the low–high band on
// the Total Spend tile, so it is stated there, in that tile's tooltip, next to the numbers it
// qualifies. Carrying it in the badge put an unexplained phrase ("range of recent errors")
// beside an unrelated figure, with no way to hover it for the context that would make it mean
// anything.
const ConfidenceBadge = ({ confidence }) => {
  const level = confidence && confidence.level;
  if (!level || level === "not_yet_rated") return null;
  const tone = TONE[level] || "border-border bg-surface-2 text-text";
  const mape = confidence.mape;
  const basisPart = BASIS_TEXT[confidence.basis] ? ` ${BASIS_TEXT[confidence.basis]}` : "";

  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${tone}`}>
      Forecast confidence: {LABEL[level] || level}
      {mape != null ? ` · ±${Math.round(mape * 100)}%${basisPart}` : ""}
    </span>
  );
};

ConfidenceBadge.propTypes = {
  confidence: PropTypes.shape({ level: PropTypes.string, mape: PropTypes.number, basis: PropTypes.string }),
};

export default ConfidenceBadge;
